const ANNOTATION_USER_PREFIX = "@user";
const ANNOTATION_USER_PATTERN = /^@user([1-9]\d*)$/;

interface ResolveAnnotationDisplayAuthorOptions {
  author: unknown;
  currentUserId: number;
  currentUserName: string;
  getUserNameById: (userId: number) => string | null;
  /** Shown instead of the internal author id when the project user cannot be resolved. */
  fallbackUserName: string;
}

export const isValidAnnotationUserId = (userId: number): boolean => {
  return Number.isSafeInteger(userId) && userId > 0;
};

export const createAnnotationAuthor = (userId: number): string => {
  if (!isValidAnnotationUserId(userId)) {
    throw new Error("A valid project user id is required for annotation ownership");
  }
  return `${ANNOTATION_USER_PREFIX}${userId}`;
};

export const parseAnnotationAuthorUserId = (author: unknown): number | null => {
  if (typeof author !== "string") {
    return null;
  }
  const match = ANNOTATION_USER_PATTERN.exec(author);
  return match ? Number(match[1]) : null;
};

export const canModifyAnnotationAsAuthor = (author: unknown, currentAuthor: string): boolean => {
  return parseAnnotationAuthorUserId(author) !== null && author === currentAuthor;
};

export const resolveAnnotationDisplayAuthor = ({
  author,
  currentUserId,
  currentUserName,
  getUserNameById,
  fallbackUserName,
}: ResolveAnnotationDisplayAuthorOptions): string => {
  const stableAuthor = typeof author === "string" ? author : "";
  const authorUserId = parseAnnotationAuthorUserId(stableAuthor);
  if (authorUserId === null) {
    return stableAuthor;
  }
  if (authorUserId === currentUserId) {
    return currentUserName || fallbackUserName;
  }
  return getUserNameById(authorUserId) || fallbackUserName;
};
